import { BlockType } from '../compiler/factory/BlockType';
import { StateNode } from '../compiler/state/StateNode';
import { CustomSchema } from '../diagram/CustomSchema';
import { CustomNode } from '../codeBlocks/CustomNode';
import { InputNode } from '../codeBlocks/InputNode';

const RECURSIVE_MAX_DEPTH: number = 500;

/**
 * Sorts roots by grouping them into 25 pixel wide columns based on x coordinate.
 * Sorts groups by horizontal coordinate.
 * Sorts roots inside the groups by vertical coordinate.
 * Merges groups into 1D array in order.
 * 
 * @param roots array of roots being sorted. (modifies)
 */
function sortRoots(roots: CustomNode[]): CustomNode[] {
    roots.sort((a, b) => {
        return a.coordinates[0] - b.coordinates[0]
    });

    let groups: CustomNode[][] = [];
    let groupTracker: [[number, number], number][] = [[[0, 20], 0]];

    for (const root of roots) {
        let xVal = root.coordinates[0];

        for (var [range, ix] of groupTracker) {
            if (range[0] <= xVal && xVal < range[1]) {
                groups[ix].push(root);
                break;
            }
        }

        groupTracker.push([[xVal - 5, xVal + 20], ix + 1]);
        groups[ix + 1] = [root];
    }

    groups.map(group => {
        group.sort((a, b) => a.coordinates[1] - b.coordinates[1])
        return group;
    });


    return groups.flat();
}


/**
 * Precompiles schema of a diagram into an array of StateNode trees,
 * in order. This array is made compatible to be compiled by the
 * compiler.
 * 
 * The precompiler is lazy: only output nodes and nested nodes
 * (such as IF, WHILE, etc.) are considered as root nodes. If
 * a node is not connected to a root node, it is exluded.
 * 
 * @param schema map of all diagram schemas in the program (must include 'main')
 * @return state node tree array.
 */
function precompile(schemas: Map<string, CustomSchema>): StateNode[] {
    console.log(schemas);
    let ms = schemas.get('main');
    console.log(ms);
    let mainSchema: CustomSchema;
    if (!ms) {
        throw Error('Main schema must be in schemas')
    } else {
        mainSchema = ms;
    }

    /**
     * Returns the root nodes of a schema if there are any.
     * A root is any node that does not have output ports, such
     * as OutputNodes, IFNodes, NestNodes, etc.
     * 
     * The roots are sorted by their coordinates. Sorting is based
     * on horizontal difference. If absolute horizontal difference of 
     * a pair of roots is less than 15, then the pair is ordered by
     * vertical difference.
     * 
     * @param schema the schema.
     * @return root nodes of schema.
     */
    function getRoots(schema: CustomSchema): CustomNode[] {
        let roots: CustomNode[] = [];
        if (schema.links == null) return roots;

        roots = schema.nodes.filter(node => 'isRoot' in node)
            // avoids duplicate nodes
            .filter(function (node: CustomNode, index: number, arr: CustomNode[]) {
                return arr.indexOf(node) === index;
            });

        let sorte = sortRoots(roots);

        console.log('roots');
        console.log(sorte);
        return sorte;
    }

    /**
     * Recursive precompiler helper. Precompiles a root CustomNode and its children recursively.
     * @param root
     */
    function precompRoot(root: CustomNode, depth: number = 0, schema: CustomSchema = mainSchema): StateNode {
        if (depth >= RECURSIVE_MAX_DEPTH) throw new Error('Looping Tree?\nReached max recursion depth: ' + RECURSIVE_MAX_DEPTH);
        depth += 1;


        if (isInput(root)) {
            // base case
            let comp = new StateNode(root.id, root.blockType, root.constInputs, true);
            console.log(`Triggered: ${comp.isTriggered}`);
            return comp;

        } else if (root.blockType === BlockType.IF) {
            // getting if children
            let ifSchema = schemas.get(root.id + 'if');
            if (!ifSchema) throw Error('Encountered If Node without schema');
            let ifSchemaRoots: CustomNode[] = getRoots(ifSchema);

            let ifChildren: StateNode[] = [];

            for (let innerRoot of ifSchemaRoots) {
                let compChild = precompRoot(innerRoot, depth, ifSchema);
                ifChildren.push(compChild);
            }

            // getting else children
            let elseSchema = schemas.get(root.id + 'else');
            if (!elseSchema) throw Error('Encountered If Node without schema');
            let elseSchemaRoots: CustomNode[] = getRoots(elseSchema);

            let elseChildren: StateNode[] = [];

            for (let innerRoot of elseSchemaRoots) {
                let compChild = precompRoot(innerRoot, depth, elseSchema);
                elseChildren.push(compChild);
            }

            // getting condition
            let condition = getChildren(root, schema);
            let compiledCondition = condition.map(child => precompRoot(child, depth, schema));

            // putting it all together
            let leIf: StateNode = new StateNode(root.id, BlockType.IF, [], false);
            leIf.children = [compiledCondition, ifChildren, elseChildren];
            return leIf;

        } else if (root.blockType === BlockType.WHILE) {
            // compiling while block
            let whileSchema = schemas.get(root.id + 'while');
            if (!whileSchema) throw Error('Encountered Nested Node without schema');
            let whileSchemaRoots: CustomNode[] = getRoots(whileSchema);

            let compiledWhile: StateNode[] = [];

            for (let innerRoot of whileSchemaRoots) {
                let compChild = precompRoot(innerRoot, depth, whileSchema);
                compiledWhile.push(compChild);
            }

            //compiling condition
            let condition = getChildren(root, schema);
            console.log("my children before precompiling: ");
            console.log(condition);
            let compiledCondition = condition.map(child => precompRoot(child, depth, schema));



            let compNest = new StateNode(root.id, BlockType.WHILE, [], false);
            compNest.children = [compiledCondition, compiledWhile];

            return compNest;

        } else {
            let myChildren = getChildren(root, schema);
            console.log("my children before precompiling: ");
            console.log(myChildren);
            let compiledChildren = myChildren.map(child => precompRoot(child, depth, schema));
            console.log("my children after precompiling: ");
            console.log(compiledChildren);
            let comp = new StateNode(root.id, root.blockType, root.constInputs, false);
            comp.children = [compiledChildren];
            console.log('value of comp\'s children: ');
            console.log(comp.children);

            return comp;
        }

    }


    /**
     * Returns children of this node.
     * @param root root node (not an input node)
     * @param schema the schema that this node is in
     */
    function getChildren(root: CustomNode, schema: CustomSchema = mainSchema): CustomNode[] {
        if (!schema.nodes.includes(root)) throw Error('This node is not in schema');

        console.log("GETTING CHILDREN!");
        let children: CustomNode[] = [];
        if (root.inputs == null) return [];


        let portIds = root.inputs.map(port => port.id);
        console.log(portIds);

        portIds.sort();

        console.log('port ids');
        console.log(portIds);

        if (portIds != null) {
            let childrenOutputPorts = portIds.map(id=> schema.links.find(link=>link.input===id)?.output);
            for (let portId of childrenOutputPorts) {
                console.log('Found a child');
                if(portId == null) throw new Error('Null port id!');
                let nodeId = portId.substring(0, portId.indexOf("-output"));
                let child = schema.nodes.find(node => node.id === nodeId);
                if (child != null) children.push(child);
            }
        }
        return children;

    }

    /**
     * Determines if the node is an input node. At the moment, only
     * instances of InputNode are considered to be inputs.
     * 
     * @param node the node
     */
    function isInput(node: CustomNode): boolean {
        return node instanceof InputNode;
    }



    let precompiledStuff = getRoots(mainSchema).map(root => precompRoot(root));
    console.log('Done precompiling! here\'s what we have:');
    console.log(precompiledStuff);
    return precompiledStuff;

}



const Precompiler = {
    sortRoots,
    precompile
}


export default Precompiler;